import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { adminApi, img } from "../lib/api";
import { Card, Title, Field, Toggle, Modal, ImageInput, Badge, act } from "./kit";

function useList(path) {
  const [list, setList] = useState([]);
  const load = () => adminApi.get(path).then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line
  return [list, load];
}

async function move(path, list, i, dir, load) {
  const j = i + dir;
  if (j < 0 || j >= list.length) return;
  const a = list[i], b = list[j];
  await adminApi.put(`${path}/${a.id}`, { order: j });
  await adminApi.put(`${path}/${b.id}`, { order: i });
  await Promise.all(list.map((x, k) => (k !== i && k !== j && x.order !== k ? adminApi.put(`${path}/${x.id}`, { order: k }) : null)));
  load();
}

function Editor({ path, item, fields, onDone }) {
  const [f, setF] = useState(item);
  const save = async () => {
    await act(() => (f.id ? adminApi.put(`${path}/${f.id}`, f) : adminApi.post(path, f)), "Uloženo");
    onDone();
  };
  return (
    <div className="space-y-3">
      {fields.map(([k, l, type]) => (
        <Field key={k} label={l}>
          {type === "image" ? <ImageInput value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`edit-${k}-upload`} /> : type === "bool" ? <Toggle checked={!!f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`edit-${k}-toggle`} /> : type === "area" ? <textarea rows={3} className="field" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-${k}-input`} /> : <input className="field" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-${k}-input`} />}
        </Field>
      ))}
      <div className="flex justify-end"><button onClick={save} className="btn-pink" data-testid="edit-save-btn">Uložit</button></div>
    </div>
  );
}

function Manager({ path, title, fields, blank, render, testid }) {
  const [list, load] = useList(path);
  const [edit, setEdit] = useState(null);
  const del = async (x) => {
    if (!window.confirm("Opravdu smazat?")) return;
    await act(() => adminApi.delete(`${path}/${x.id}`), "Smazáno");
    load();
  };
  return (
    <div data-testid={testid}>
      <Title right={<button className="btn-pink" onClick={() => setEdit({ ...blank, order: list.length })} data-testid={`${testid}-new-btn`}><Plus className="w-4 h-4" /> Přidat</button>}>{title}</Title>
      <div className="space-y-2">
        {list.map((x, i) => (
          <Card key={x.id} className="!p-3 flex items-center gap-3" data-testid={`${testid}-row-${i}`}>
            {render(x)}
            <div className="ml-auto flex">
              <button onClick={() => move(path, list, i, -1, load)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]" aria-label="Nahoru"><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(path, list, i, 1, load)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]" aria-label="Dolů"><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => setEdit(x)} data-testid={`${testid}-edit-${i}`} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(x)} data-testid={`${testid}-delete-${i}`} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Upravit" : "Přidat"}>
        {edit && <Editor path={path} item={edit} fields={fields} onDone={() => { setEdit(null); load(); }} />}
      </Modal>
    </div>
  );
}

export const Categories = () => (
  <Manager path="/categories" title="Kategorie" testid="admin-categories" blank={{ name: "", image: "", description: "", show_on_home: true, is_free: false }}
    fields={[["name", "Název"], ["slug", "URL (slug)"], ["description", "Popis", "area"], ["image", "Obrázek (kruh na hlavní stránce)", "image"], ["show_on_home", "Zobrazit na hlavní stránce", "bool"], ["is_free", "Materiály zdarma (odemčení přes newsletter)", "bool"]]}
    render={(c) => (<><img src={img(c.image)} alt="" className="w-12 h-12 rounded-full object-cover" /><div><p className="font-bold">{c.name}</p><div className="flex gap-1 mt-1">{c.show_on_home && <Badge tone="teal">Hlavní stránka</Badge>}{c.is_free && <Badge tone="sun">Zdarma</Badge>}</div></div></>)} />
);

export const Slides = () => (
  <Manager path="/slides" title="Hero slidy" testid="admin-slides" blank={{ title: "", subtitle: "", badge: "PRO DĚTI V MŠ", button_text: "Prozkoumat materiály", button_link: "/eshop", image: "", active: true }}
    fields={[["title", "Nadpis"], ["subtitle", "Text", "area"], ["badge", "Malý štítek"], ["button_text", "Text tlačítka"], ["button_link", "Odkaz tlačítka"], ["image", "Obrázek", "image"], ["active", "Aktivní", "bool"]]}
    render={(s) => (<><img src={img(s.image)} alt="" className="w-20 h-14 rounded-xl object-cover" /><div><p className="font-bold">{s.title}</p><p className="text-xs text-[var(--muted)] line-clamp-1">{s.subtitle}</p>{!s.active && <Badge tone="gray">Skryto</Badge>}</div></>)} />
);
